import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";

const bullets = [
  { icon: "ri-hand-coin-line", text: "Tap-to-pay in seconds" },
  { icon: "ri-store-2-line", text: "Built for vendors, events, and gig workers" },
  { icon: "ri-smartphone-line", text: "No hardware required" },
  { icon: "ri-flashlight-line", text: "Instant payouts and real-time tracking" },
];

/* ─── Realistic Animated Phone ─── */
function PhoneMockup() {
  const [phase, setPhase] = useState<"idle" | "tapping" | "confirmed">("idle");
  const [amount] = useState("24.00");

  useEffect(() => {
    const cycle = () => {
      setPhase("idle");
      const t1 = setTimeout(() => setPhase("tapping"), 2200);
      const t2 = setTimeout(() => setPhase("confirmed"), 3400);
      const t3 = setTimeout(cycle, 6200);
      return [t1, t2, t3];
    };
    const timers = cycle();
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div className="relative flex items-center justify-center select-none">
      {/* Ambient glow */}
      <div
        className="absolute w-80 h-80 rounded-full blur-[80px] transition-all duration-1000"
        style={{
          background: phase === "confirmed"
            ? "radial-gradient(circle, rgba(16,185,129,0.25) 0%, transparent 70%)"
            : "radial-gradient(circle, rgba(99,102,241,0.2) 0%, transparent 70%)",
        }}
      />

      {/* Phone Frame — iPhone-style */}
      <div
        className="relative w-[290px] rounded-[3rem] overflow-hidden transition-transform duration-500"
        style={{
          border: "6px solid #1a1a1f",
          background: "#0c0c0f",
          boxShadow: `
            0 0 0 1px rgba(255,255,255,0.06) inset,
            0 40px 100px rgba(0,0,0,0.35),
            0 10px 30px rgba(99,102,241,0.12),
            -8px 0 20px rgba(0,0,0,0.1),
            8px 0 20px rgba(0,0,0,0.1)
          `,
          transform: phase === "tapping" ? "scale(1.02)" : "scale(1)",
        }}
      >
        {/* Dynamic Island */}
        <div className="flex justify-center pt-3 pb-1">
          <div
            className="rounded-full bg-black transition-all duration-500 flex items-center justify-center"
            style={{
              width: phase === "tapping" ? "100px" : "84px",
              height: "26px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.4) inset",
            }}
          >
            {phase === "tapping" && (
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                <span className="text-[8px] text-green-400 font-semibold tracking-wide">NFC</span>
              </div>
            )}
          </div>
        </div>

        {/* Screen */}
        <div className="px-5 pb-8 pt-4 min-h-[540px] flex flex-col items-center justify-center">
          {phase === "idle" && (
            <div className="flex flex-col items-center gap-4 animate-fadeIn">
              {/* Tapn branding */}
              <div className="flex items-center gap-1 mb-1">
                <div className="w-5 h-5 rounded-md flex items-center justify-center"
                  style={{ background: "linear-gradient(135deg, #6366F1, #3B82F6)" }}>
                  <span className="text-[8px] font-black text-white">t</span>
                </div>
                <span className="text-[11px] font-bold text-white/60 tracking-wider uppercase">Tapn</span>
              </div>

              <p className="text-[10px] font-semibold tracking-[0.2em] text-indigo-300/70 uppercase">
                Ready to receive
              </p>

              {/* NFC Ring */}
              <div
                className="w-32 h-32 rounded-full flex items-center justify-center"
                style={{
                  background: "rgba(99,102,241,0.08)",
                  border: "1px solid rgba(99,102,241,0.15)",
                }}
              >
                <div className="w-20 h-20 rounded-full flex items-center justify-center"
                  style={{
                    background: "rgba(99,102,241,0.1)",
                    border: "1px solid rgba(99,102,241,0.12)",
                  }}>
                  <i className="ri-wifi-line text-3xl text-indigo-400/80" />
                </div>
              </div>

              <div className="text-center mt-1">
                <div className="text-white text-2xl font-bold tracking-tight">${amount}</div>
                <div className="text-white/30 text-[10px] mt-1">Hold device near to pay</div>
              </div>

              {/* Bottom bar */}
              <div className="w-full flex justify-between items-center mt-2 px-2">
                <div className="flex items-center gap-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                  <span className="text-[9px] text-white/40">Online</span>
                </div>
                <span className="text-[9px] text-white/25 font-mono">POS-0192</span>
              </div>
            </div>
          )}

          {phase === "tapping" && (
            <div className="flex flex-col items-center gap-4 animate-fadeIn">
              <p className="text-[10px] font-semibold tracking-[0.2em] text-amber-300/80 uppercase">
                Processing
              </p>

              {/* Active NFC pulse */}
              <div className="relative w-32 h-32 flex items-center justify-center">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="absolute rounded-full"
                    style={{
                      width: `${60 + i * 24}px`,
                      height: `${60 + i * 24}px`,
                      border: "1.5px solid rgba(99,102,241,0.4)",
                      animation: `pulseRing 1.2s ease-out ${i * 0.25}s infinite`,
                    }}
                  />
                ))}
                <div
                  className="w-20 h-20 rounded-full flex items-center justify-center z-10"
                  style={{
                    background: "linear-gradient(135deg, #6366F1, #14B8A6)",
                    boxShadow: "0 0 40px rgba(99,102,241,0.5), 0 0 80px rgba(99,102,241,0.2)",
                  }}
                >
                  <i className="ri-wifi-line text-3xl text-white" />
                </div>
              </div>

              <div className="text-center mt-1">
                <div className="text-white text-2xl font-bold tracking-tight">${amount}</div>
                <div className="text-amber-300/60 text-[10px] mt-1 flex items-center gap-1 justify-center">
                  <div className="w-1 h-1 rounded-full bg-amber-300 animate-pulse" />
                  Authenticating...
                </div>
              </div>

              <div className="w-full bg-white/5 rounded-full h-1 mt-2 overflow-hidden">
                <div className="h-full rounded-full animate-progressBar"
                  style={{ background: "linear-gradient(90deg, #6366F1, #14B8A6)" }} />
              </div>
            </div>
          )}

          {phase === "confirmed" && (
            <div className="flex flex-col items-center gap-3 animate-fadeIn">
              {/* Success badge */}
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center animate-scaleIn"
                style={{
                  background: "linear-gradient(135deg, #10B981, #059669)",
                  boxShadow: "0 0 30px rgba(16,185,129,0.4), 0 0 60px rgba(16,185,129,0.15)",
                }}
              >
                <i className="ri-check-line text-white text-2xl" />
              </div>

              <div className="text-green-400 font-bold text-sm tracking-wide">Payment Confirmed</div>
              <div className="text-white text-3xl font-bold tracking-tight">${amount}</div>

              {/* Transaction detail card */}
              <div className="w-full bg-white/[0.04] rounded-xl border border-white/[0.06] p-3 mt-1 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-[10px] text-white/40">Via</span>
                  <span className="text-[10px] font-semibold text-indigo-300">Tapn × Paygent</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[10px] text-white/40">Ref</span>
                  <span className="text-[10px] font-mono text-white/60">TPN-8821</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[10px] text-white/40">Payout</span>
                  <span className="text-[10px] font-semibold text-green-400 flex items-center gap-1">
                    <div className="w-1 h-1 rounded-full bg-green-400" />
                    Instant
                  </span>
                </div>
              </div>

              <div className="text-[9px] text-white/20 mt-1">Powered by Paygent Infrastructure</div>
            </div>
          )}
        </div>

        {/* Home indicator bar */}
        <div className="flex justify-center pb-3">
          <div className="w-28 h-1 rounded-full bg-white/15" />
        </div>
      </div>

      {/* Floating badges */}
      <div
        className="absolute -bottom-4 -left-6 flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold"
        style={{
          background: "white",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.04)",
          color: "#0F766E",
        }}
      >
        <i className="ri-flashlight-fill text-teal-500" />
        &lt;2s tap
      </div>

      <div
        className="absolute -top-3 -right-4 flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-semibold"
        style={{
          background: "white",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.04)",
          color: "#4338CA",
        }}
      >
        <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
        Live
      </div>

      <style>{`
        @keyframes pulseRing {
          0% { opacity: 0.6; transform: scale(1); }
          100% { opacity: 0; transform: scale(1.8); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes scaleIn {
          0% { opacity: 0; transform: scale(0.5); }
          60% { transform: scale(1.1); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes progressBar {
          0% { width: 0%; }
          100% { width: 100%; }
        }
        .animate-fadeIn { animation: fadeIn 0.4s ease-out; }
        .animate-scaleIn { animation: scaleIn 0.5s ease-out; }
        .animate-progressBar { animation: progressBar 1.2s ease-out; }
      `}</style>
    </div>
  );
}

/* ─── Main Section ─── */
export default function TapnSection() {
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const [showInterstitial, setShowInterstitial] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const commercialRef = useRef<HTMLVideoElement>(null);
  const navigate = useNavigate();

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
        setIsPlaying(true);
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  const handleStartTapn = () => {
    setShowInterstitial(true);
    document.body.style.overflow = "hidden";
  };

  const handleInterstitialEnd = () => {
    setShowInterstitial(false);
    document.body.style.overflow = "";
    navigate("/tapn");
  };

  return (
    <section className="py-16 md:py-28 overflow-hidden" style={{ background: "linear-gradient(135deg, #F8F9FF 0%, #F0FDF9 50%, #F8F9FF 100%)" }}>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-8">

        {/* ─── TOP ROW: Text + Animated Phone ─── */}
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20 mb-16 lg:mb-24">

          {/* LEFT: Content */}
          <div className="flex-1 max-w-xl flex-shrink-0">
            {/* Label */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6"
              style={{ background: "linear-gradient(90deg, rgba(99,102,241,0.1), rgba(20,184,166,0.1))", border: "1px solid rgba(99,102,241,0.2)" }}>
              <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />
              <span className="text-xs font-bold tracking-widest uppercase" style={{ color: "#6366F1" }}>Powered by Paygent</span>
            </div>

            {/* Tapn logo */}
            <div className="mb-8">
              <img
                src="/tapn-logo.png"
                alt="Tapn — Tap. Pay. Done."
                className="h-[32rem] md:h-[40rem] w-auto object-contain -ml-4"
                style={{ mixBlendMode: "multiply" }}
              />
            </div>

            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">
              Real-world payments,<br />
              <span style={{ background: "linear-gradient(90deg, #6366F1, #14B8A6)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                simplified.
              </span>
            </h2>

            <p className="text-gray-500 text-base leading-relaxed mb-8">
              Tapn is the fastest way for businesses, vendors, and platforms to accept payments anywhere. Built on Paygent, it turns complex payment infrastructure into a seamless tap-and-go experience.
            </p>

            {/* Bullets */}
            <ul className="space-y-3 mb-10">
              {bullets.map((b) => (
                <li key={b.text} className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
                    style={{ background: "linear-gradient(135deg, rgba(99,102,241,0.12), rgba(20,184,166,0.12))" }}>
                    <i className={`${b.icon} text-sm`} style={{ color: "#6366F1" }} />
                  </div>
                  <span className="text-sm font-medium text-gray-700">{b.text}</span>
                </li>
              ))}
            </ul>

            {/* CTA */}
            <button
              onClick={handleStartTapn}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl text-white text-sm font-bold transition-all duration-200 hover:opacity-90 hover:-translate-y-0.5 whitespace-nowrap cursor-pointer"
              style={{ background: "linear-gradient(135deg, #6366F1, #14B8A6)", boxShadow: "0 8px 24px rgba(99,102,241,0.3)" }}
            >
              <i className="ri-hand-coin-line w-4 h-4 flex items-center justify-center" />
              Start using Tapn
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
            </button>
          </div>

          {/* RIGHT: Animated Phone */}
          <div className="flex-1 flex items-center justify-center">
            <PhoneMockup />
          </div>
        </div>

        {/* ─── BOTTOM ROW: Video Hero with Gradient Frame ─── */}
        <div
          className="rounded-[1.75rem] p-[3px]"
          style={{
            background: "linear-gradient(135deg, #6366F1, #818CF8, #14B8A6, #6366F1)",
            backgroundSize: "300% 300%",
            animation: "gradientShift 6s ease infinite",
          }}
        >
          <div className="relative rounded-3xl overflow-hidden w-full aspect-video bg-gray-900 group">
            {/* Glass reflection overlay */}
            <div className="absolute inset-0 bg-gradient-to-tr from-white/5 to-transparent z-10 pointer-events-none mix-blend-overlay" />

            {/* Corner label */}
            <div className="absolute top-5 left-5 z-20 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/30 backdrop-blur-md border border-white/10">
              <img src="/tapn-logo.png" alt="Tapn" className="h-4 w-auto opacity-90" style={{ filter: "brightness(10)" }} />
              <span className="text-[10px] font-bold tracking-wider text-white/80 uppercase">x Paygent</span>
            </div>

            <video
              ref={videoRef}
              autoPlay
              loop
              muted={isMuted}
              playsInline
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
              src="/videos/paygent-tapn.mp4"
            />

            {/* Controls */}
            <div className="absolute bottom-5 right-5 z-20 flex items-center gap-2">
              <button
                onClick={togglePlay}
                className="w-11 h-11 rounded-full bg-black/40 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-black/60 transition-colors cursor-pointer"
                aria-label={isPlaying ? "Pause video" : "Play video"}
              >
                <i className={isPlaying ? "ri-pause-line text-lg" : "ri-play-line text-lg"} />
              </button>
              <button
                onClick={toggleMute}
                className="w-11 h-11 rounded-full bg-black/40 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-black/60 transition-colors cursor-pointer"
                aria-label="Toggle mute"
              >
                <i className={isMuted ? "ri-volume-mute-line text-lg" : "ri-volume-up-line text-lg"} />
              </button>
            </div>
          </div>
        </div>

        <style>{`
          @keyframes gradientShift {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}</style>

      </div>

      {/* ─── Fullscreen Video Interstitial ─── */}
      {showInterstitial && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center"
          style={{ background: "#000" }}
        >
          <video
            ref={commercialRef}
            autoPlay
            playsInline
            onEnded={handleInterstitialEnd}
            className="w-full h-full object-contain"
            src="/videos/tapn-commercial.mp4"
          />

          {/* Skip button */}
          <button
            onClick={handleInterstitialEnd}
            className="absolute top-6 right-6 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-sm font-medium hover:bg-white/20 transition-colors cursor-pointer flex items-center gap-2"
          >
            Skip
            <i className="ri-skip-forward-line" />
          </button>

          {/* Bottom branding */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2 opacity-40">
            <img src="/tapn-logo.png" alt="Tapn" className="h-5 w-auto" style={{ filter: "brightness(10)" }} />
          </div>
        </div>
      )}
    </section>
  );
}
